/**
 * Translate/extension DOM-mutation guard.
 *
 * Google Translate (and DOM-injecting browser extensions) rewrap text nodes in
 * their own `<font>` elements. React still holds references to the ORIGINAL
 * nodes, so its commit phase calls `removeChild` / `insertBefore` against a
 * parent that no longer owns them and the DOM throws `NotFoundError`. React
 * has no recovery for that — it propagates to the nearest error boundary, so a
 * single translated page turns every subsequent render into a crash screen.
 *
 * Observed in production 2026-08-29: one user switched Chrome to "Translate to
 * Chinese" six minutes after signup and hit 65 boundary crashes across every
 * route in 31 minutes — dashboard, contacts, campaigns, sms-sender,
 * conversations, settings. The app was unusable for their entire first session.
 *
 * The fix (facebook/react#11538, the React team's own recommended workaround)
 * is to make the two mutation methods no-op when the node isn't actually a
 * child, instead of throwing. React's next render reconciles from its own tree
 * and the page recovers on its own.
 *
 * We deliberately do NOT set `translate="no"` on <html>: that also stops the
 * crash, but by taking translation away from the non-English speakers who need
 * it. Staying translatable is the point.
 *
 * ponytail: monkeypatches two Node.prototype methods, so it would also mask a
 * genuine app bug of the same shape. React never legitimately removes a node
 * from the wrong parent, so that trade is currently free — revisit if React
 * ships its own translation resilience.
 */

import { reportClientError } from "@/lib/clientErrorReporter";

const GUARD_FLAG = "__vrDomMutationGuard";

/** One report per page load — enough to measure how often this fires, never a flood. */
let reported = false;

function reportOnce(method: "removeChild" | "insertBefore") {
  if (reported) return;
  reported = true;
  try {
    reportClientError({
      error: new Error(
        `Foreign DOM mutation suppressed in ${method} — page is translated or ` +
          `mutated by an extension. Render was allowed to continue.`,
      ),
      source: "window",
      section: "translate-dom-guard",
    });
  } catch {
    // Reporting must never be the thing that breaks the render.
  }
}

/**
 * Idempotent — safe to call on every module evaluation (HMR, double-import).
 * No-ops outside the browser.
 */
export function installDomMutationGuard(): void {
  if (typeof window === "undefined" || typeof Node === "undefined") return;

  const proto = Node.prototype as Node & Record<string, unknown>;
  if (proto[GUARD_FLAG]) return;
  proto[GUARD_FLAG] = true;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function removeChild<T extends Node>(
    this: Node,
    child: T,
  ): T {
    if (child.parentNode !== this) {
      reportOnce("removeChild");
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function insertBefore<T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      reportOnce("insertBefore");
      return newNode;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}
